import { ItemsPerPage } from '@billy/design-system'
import { useEffect, useMemo, useRef } from 'react'
import { useQuery } from 'react-query'

import { Contact } from '@features/contacts/types/contact'
import { InvoiceStatus } from '@features/invoicesLegacy/enums/invoiceStatus'
import { Invoice } from '@features/invoicesLegacy/types/invoice'
import { InvoiceSendingState } from '@features/invoicesLegacy/types/invoiceSendingState'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { SortDirection } from '../../../../../enums/sortDirection'
import { usePaginationPrefetching } from '../../../../../hooks/usePaginationPrefetching'
import { Paging } from '../../../../../types/metable'
import { invoiceStatusToQueryProps } from '../../../constants/invoiceStatusToQueryProps'
import { fetchInvoices, FetchInvoicesResponseData, InvoicesSortProperty } from '../../../query-api'
import { InvoicesQueryProps } from '../../../types/invoicesQueryProps'
import { getInvoicesQueryKey } from '../../../utils/getInvoicesQueryKey'
import { InvoicesSummaryQueryProps } from '../types/InvoicesSummaryQueryProps'

interface UseFetchInvoicesProps {
  contactId?: string
  page?: number
  pageSize?: ItemsPerPage
  period?: string
  searchQuery?: string
  sendingState?: InvoiceSendingState
  sortDirection?: SortDirection
  sortProperty?: InvoicesSortProperty
  status?: InvoiceStatus
  currencyId?: string
  enabled?: boolean
}

interface UseFetchInvoicesResponse {
  invoices: Invoice[]
  contacts: Contact[]
  isLoading: boolean
  pagination?: Paging
}

export const useFetchInvoices = ({
  contactId,
  page,
  pageSize,
  period,
  searchQuery,
  sendingState,
  sortDirection,
  sortProperty,
  status,
  currencyId,
  enabled = true,
}: UseFetchInvoicesProps): UseFetchInvoicesResponse => {
  const { organization } = useUserOrganization()
  const previousPagination = useRef<Paging | undefined>(undefined)
  const isQueryEnabled = enabled && !!organization

  const queryProps: InvoicesSummaryQueryProps = useMemo(
    () => ({
      ...(status ? invoiceStatusToQueryProps[status] : {}),
      ...(contactId ? { contactId } : {}),
      ...(currencyId ? { currencyId } : {}),
      entryDatePeriod: period,
      organizationId: organization?.id as string,
      page,
      pageSize: pageSize as ItemsPerPage,
      sendingState,
      sortDirection,
      sortProperty,
      q: searchQuery,
    }),
    [
      status,
      contactId,
      period,
      organization?.id,
      page,
      pageSize,
      sendingState,
      sortDirection,
      sortProperty,
      searchQuery,
      currencyId,
    ],
  )
  const queryKey = useMemo(() => getInvoicesQueryKey(queryProps), [queryProps])

  const { data, isIdle, isLoading } = useQuery(queryKey, () => fetchInvoices(queryProps), {
    enabled: isQueryEnabled,
  })

  // If pagination exists then copy the value to reference and use it when original pagination during loading returns `undefined` value.
  useEffect(() => {
    if (data?.meta.paging) {
      previousPagination.current = { ...data?.meta.paging }
    }
  }, [data?.meta.paging])

  usePaginationPrefetching<InvoicesQueryProps, FetchInvoicesResponseData>({
    getQueryFunction: useMemo(() => fetchInvoices, []),
    getQueryKey: useMemo(() => getInvoicesQueryKey, []),
    isEnabled: isQueryEnabled && !isLoading,
    queryProps,
    totalPages: data?.meta.paging.pageCount as number,
  })

  return {
    invoices: data?.invoices || [],
    contacts: data?.contacts || [],
    isLoading: enabled && (isIdle || isLoading),
    pagination: data?.meta.paging || previousPagination.current,
  }
}
